import React, { useEffect, Suspense, lazy } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftIcon, MapPinIcon, ArrowUpRightIcon, MailIcon, StarIcon, CheckIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SISTER_ORGANIZATIONS } from '../data/sister-organizations';
import { NetworkOrganizations } from '../components/NetworkOrganizations';
import { PILLARS, PARTICIPATION, JOIN_STEPS, PRESIDENT_LETTER } from '../data/national-network-content';
const NetworkMap = lazy(() => import('../components/NetworkMap').then(m => ({
  default: m.NetworkMap
})));
const NETWORK_LOGO = 'https://cdn.magicpatterns.com/uploads/eZ5rmta1LUT5zNjEpkMWnd/HAA_National_Network.png';
const HAA_LOGO = 'https://cdn.magicpatterns.com/uploads/b1GB8Z9aEvoE58p9BdX4qx/HonduranAmericanLogoNew.png';
const CONTACT_EMAIL = 'eva@honduranamerican.org';
const NAVY = '#0b1f3f';
const NAVY_DEEP = '#071427';
const BLUE = '#1b5fbe';
const SKY = '#3FA9F5';
const RED = '#e0343c';
const PALE = '#eef3fa';
const EASE = [0.16, 1, 0.3, 1] as const;
function SectionLabel({
  children,
  onDark = false
}: {
  children: React.ReactNode;
  onDark?: boolean;
}) {
  return <p className="inline-flex items-center gap-2.5 font-nunito text-[10px] font-bold uppercase tracking-[0.3em] mb-5" style={{
    color: onDark ? SKY : BLUE
  }} data-id="element-3853" data-mp="2c7agh0s7mlfg">
      <StarIcon className="w-3 h-3" style={{
      color: RED
    }} fill={RED} data-id="element-3854" data-mp="237dytj48mmtw" />
      {children}
    </p>;
}
export function NationalNetworkPage() {
  const {
    language,
    setLanguage
  } = useLanguage();
  const isEs = language === 'ES';
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const mailto = (subject: string) => `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}`;
  const joinHref = mailto(isEs ? 'Solicitud de Membresía — Red Nacional Hondureño-Americana' : 'Membership Application — Honduran-American National Network');
  return <div className="min-h-screen w-full font-nunito antialiased" style={{
    backgroundColor: '#ffffff',
    color: NAVY
  }} data-id="element-3855" data-mp="3jjt0ouhk8bej">
      {/* Header — white bar carrying the National Network lockup */}
      <header className="sticky top-0 z-30 w-full bg-white shadow-sm" data-id="element-3856" data-mp="1lrjll7vg7j2l">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between gap-4" data-id="element-3857" data-mp="1804zgs68v1rb">
          <a href="#" className="flex items-center group" aria-label="Honduran American Association" data-id="element-3858" data-mp="1c05b651ykyuo">
            <img src={HAA_LOGO} alt="Honduran American Association" className="h-10 sm:h-12 w-auto object-contain group-hover:opacity-80 transition-opacity" data-id="element-3859" data-mp="24p2t2729ctmw" />
          </a>
          <div className="flex items-center gap-4" data-id="element-3860" data-mp="1vmjsxupuwv4c">
            <div className="inline-flex rounded-full border overflow-hidden" style={{
            borderColor: 'rgba(11,31,63,0.25)'
          }} role="group" aria-label={isEs ? 'Idioma' : 'Language'} data-id="element-3861" data-mp="f9mn10jmro18">
              {(['EN', 'ES'] as const).map(lang => <button key={lang} type="button" onClick={() => setLanguage(lang)} aria-pressed={language === lang} className="px-3.5 py-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-colors" style={{
              backgroundColor: language === lang ? NAVY : 'transparent',
              color: language === lang ? '#ffffff' : 'rgba(11,31,63,0.6)'
            }} data-id="element-3862" data-mp="7qdndz0xf3pd">
                  {lang}
                </button>)}
            </div>
            <a href={joinHref} className="hidden sm:inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-white text-[11px] font-bold uppercase tracking-[0.16em] transition-colors" style={{
            backgroundColor: NAVY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = NAVY;
          }} data-id="element-3863" data-mp="3u5etcf1mc8xf">
              {isEs ? 'Únase' : 'Join'}
            </a>
            <a href="#" className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.16em] transition-opacity hover:opacity-60" style={{
            color: NAVY
          }} data-id="element-3864" data-mp="2ih0up4qct3fd">
              <ArrowLeftIcon className="w-3.5 h-3.5" data-id="element-3865" data-mp="2sef9z10jjm63" />
              {isEs ? 'Volver' : 'Back'}
            </a>
          </div>
        </div>
        <div className="h-[3px] w-full flex" data-id="element-3866" data-mp="2xf2y9exdxowm">
          <div className="flex-1" style={{
          backgroundColor: BLUE
        }} data-id="element-3867" data-mp="31getq12xx4cq" />
          <div className="flex-1 bg-white" data-id="element-3868" data-mp="31dk7c40d99go" />
          <div className="flex-1" style={{
          backgroundColor: RED
        }} data-id="element-3869" data-mp="131bu2uxtrx8k" />
        </div>
      </header>

      <main data-id="element-3870" data-mp="1713wsr0jf960">
        {/* 1 — Hero */}
        <section className="relative overflow-hidden" style={{
        backgroundColor: NAVY
      }} data-id="element-3871" data-mp="1725evffz0vx8">
          <div aria-hidden="true" className="absolute inset-0 opacity-[0.13]" style={{
          backgroundImage: 'linear-gradient(to right, rgba(255,255,255,0.25) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.25) 1px, transparent 1px)',
          backgroundSize: '64px 64px',
          maskImage: 'radial-gradient(ellipse at 50% 35%, black 15%, transparent 72%)',
          WebkitMaskImage: 'radial-gradient(ellipse at 50% 35%, black 15%, transparent 72%)'
        }} data-id="element-3872" data-mp="zk78hjt360y8" />
          <div className="relative max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-16 lg:pt-20 lg:pb-20 text-center" data-id="element-3873" data-mp="14hfslpxsof24">
            <motion.div initial={{
            opacity: 0,
            y: 18
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            ease: EASE
          }} data-id="element-3874" data-mp="3u7at92i36t4z">
              <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl tracking-[0.03em] leading-[1.02] mb-8" data-id="element-3875" data-mp="3qou2uu898dpe">
                <span className="block" style={{
                color: SKY
              }} data-id="element-3876" data-mp="3vogjldnakc2y">
                  {isEs ? 'Organizaciones Más Fuertes' : 'Stronger Organizations'}
                </span>
                <span className="block text-white" data-id="element-3877" data-mp="3lozdiv44nw6g">
                  {isEs ? 'Mayor Conexión' : 'Greater Connection'}
                </span>
                <span className="block" style={{
                color: RED
              }} data-id="element-3878" data-mp="37x1cz8ohf8lw">
                  {isEs ? '¡Impacto Binacional!' : 'Bi-National Impact!'}
                </span>
              </h1>
              <p className="font-nunito text-white/70 text-lg leading-relaxed max-w-2xl mx-auto mb-9" data-id="element-3879" data-mp="2y1hfwiy6m3vk">
                {isEs ? 'Un espacio nacional que conecta a las organizaciones, líderes y socios que ya sirven a las comunidades hondureñas en los Estados Unidos y en Honduras.' : 'A national space connecting the organizations, leaders, and partners already serving Honduran communities in the United States and in Honduras.'}
              </p>
              <a href={joinHref} className="group inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
              color: NAVY
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = RED;
              e.currentTarget.style.color = '#ffffff';
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = NAVY;
            }} data-id="element-3880" data-mp="25eq73j9ou5lj">
                {isEs ? 'Únase a la Red' : 'Join the Network'}
                <ArrowUpRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-3881" data-mp="3c1dctvz9iszl" />
              </a>
            </motion.div>
          </div>
        </section>

        {/* Logo band */}
        <section className="bg-white" data-id="element-3882" data-mp="ic7qf3rr0w7j">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 lg:py-7 flex justify-center" data-id="element-3883" data-mp="n8op2rwkqsyv">
            <motion.img initial={{
            opacity: 0,
            y: 14
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.5,
            ease: EASE
          }} src="https://cdn.magicpatterns.com/uploads/xp6mxLQyMd62ZaHp36n7MC/HAA_National_Network.png" alt="Honduran American Association National Network" className="w-[clamp(18rem,58vw,44rem)] h-auto object-contain" data-id="element-3884" data-mp="rb1konhlxahx" />
          </div>
        </section>

        {/* 2 — Letter from the President */}
        <section style={{
        backgroundColor: PALE
      }} data-id="element-3885" data-mp="dcv54ngizx72">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-id="element-3886" data-mp="3bzze2iifgey2">
            <SectionLabel data-id="element-3887" data-mp="3gzlo6j2ylf2n">
              {isEs ? 'Carta del Presidente' : 'Letter from the President'}
            </SectionLabel>
            <p className="font-bebas text-3xl tracking-[0.04em] mb-7" style={{
            color: BLUE
          }} data-id="element-3888" data-mp="39f9qmpoils1d">
              {isEs ? PRESIDENT_LETTER.salutationEs : PRESIDENT_LETTER.salutation}
            </p>
            <div className="space-y-5 font-nunito text-base sm:text-[1.0625rem] leading-relaxed" style={{
            color: 'rgba(11,31,63,0.78)'
          }} data-id="element-3889" data-mp="2vhcqxkerl5ih">
              {(isEs ? PRESIDENT_LETTER.paragraphsEs : PRESIDENT_LETTER.paragraphs).map(p => <p key={p.slice(0, 40)} data-id="element-3890" data-mp="2gsymx65m3b0c">{p}</p>)}
            </div>
            <div className="mt-10 pt-7 border-t" style={{
            borderColor: 'rgba(11,31,63,0.15)'
          }} data-id="element-3891" data-mp="2btls9z8i0zn7">
              <p className="font-nunito text-sm mb-7" style={{
              color: 'rgba(11,31,63,0.6)'
            }} data-id="element-3892" data-mp="27qfp50gonoky">
                {isEs ? PRESIDENT_LETTER.closingEs : PRESIDENT_LETTER.closing}
              </p>
              <div className="grid sm:grid-cols-2 gap-8 sm:gap-6" data-id="element-3893" data-mp="cqlv5ujw0ksl">
                {[{
                src: 'https://cdn.magicpatterns.com/uploads/mcPsQzGFDU6t8Dm21iMLQ5/154111606_32s61fjkqp-GREY.jpg',
                name: PRESIDENT_LETTER.name,
                role: isEs ? PRESIDENT_LETTER.roleEs : PRESIDENT_LETTER.role,
                position: '50% 22%',
                zoom: 1.28
              }, {
                src: 'https://cdn.magicpatterns.com/uploads/kb8rqEBdvwMudH3CyTb87Y/ECallejas.jpg',
                name: 'Eva Callejas',
                role: isEs ? 'Vicepresidenta de Alianzas e Iniciativas Estratégicas' : 'Vice President of Partnerships & Strategic Initiatives',
                position: '50% 18%',
                zoom: 1
              }].map(person => <div key={person.name} className="flex items-center gap-5 sm:gap-6" data-id="element-3894" data-mp="1mqomh6ecaegq">
                    <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full overflow-hidden flex-shrink-0" style={{
                  border: `3px solid ${BLUE}`
                }} data-id="element-3895" data-mp="1dop4aarl94c7">
                      <img src={person.src} alt={`${person.name}, ${person.role}`} className="w-full h-full object-cover" style={{
                    objectPosition: person.position,
                    transform: `scale(${person.zoom})`
                  }} data-id="element-3896" data-mp="25g4ltqeag0vk" />
                    </div>
                    <div data-id="element-3897" data-mp="2obkzrvkql06e">
                      <p className="font-bebas text-2xl tracking-[0.04em] leading-tight" style={{
                    color: NAVY
                  }} data-id="element-3898" data-mp="2t4e7msbi4omp">
                        {person.name}
                      </p>
                      <p className="font-nunito text-sm leading-snug" style={{
                    color: 'rgba(11,31,63,0.6)'
                  }} data-id="element-3899" data-mp="2z043fkfoui6n">
                        {person.role}
                      </p>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* 3 — Pillars of Collaboration */}
        <section style={{
        backgroundColor: NAVY_DEEP
      }} data-id="element-3900" data-mp="2f8pf5b73ma1x">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3901" data-mp="2ba9tbamr77q3">
            <div className="text-center max-w-2xl mx-auto mb-14" data-id="element-3902" data-mp="26anewxv6vlfp">
              <SectionLabel onDark data-id="element-3903" data-mp="268aqc1uxecto">
                {isEs ? '01 — La Red' : '01 — The Network'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" style={{
              color: '#ffffff'
            }} data-id="element-3904" data-mp="1wbftroi7qmi1">
                {isEs ? 'Pilares de Colaboración' : 'Pillars of Collaboration'}
              </h2>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12" data-id="element-3905" data-mp="1w6gif9891d8m">
              {PILLARS.map((pillar, i) => {
              const Icon = pillar.icon;
              return <motion.article key={pillar.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: Math.min(i, 3) * 0.06,
                ease: EASE
              }} className="relative pl-14" data-id="element-3906" data-mp="13hrp3wdfqc7f" data-mpi={i}>
                    <span className="absolute left-0 top-0 w-9 h-9 rounded-full flex items-center justify-center font-bebas text-base tracking-wide text-white" style={{
                  backgroundColor: i % 2 === 0 ? SKY : 'rgba(255,255,255,0.14)'
                }} aria-hidden="true" data-id="element-3907" data-mp="pq4qvqzxmtm0">
                      {i + 1}
                    </span>
                    <Icon className="w-6 h-6 mb-4" style={{
                  color: SKY
                }} strokeWidth={1.6} data-id="element-3908" data-mp="2wd2fjfynjpsk" />
                    <h3 className="font-bebas text-2xl tracking-[0.04em] leading-tight mb-3 text-white" data-id="element-3909" data-mp="363f0386gviyp">
                      {isEs ? pillar.titleEs : pillar.title}
                    </h3>
                    <p className="font-nunito text-sm leading-relaxed" style={{
                  color: 'rgba(255,255,255,0.68)'
                }} data-id="element-3910" data-mp="3f320bqngmiq6">
                      {isEs ? pillar.textEs : pillar.text}
                    </p>
                  </motion.article>;
            })}
            </div>
          </div>
        </section>

        {/* 2 — Map + member organizations */}
        <section style={{
        backgroundColor: NAVY
      }} data-id="element-3911" data-mp="3moxgviey2uwx">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3912" data-mp="3mo4708kovpak">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-10" data-id="element-3913" data-mp="3rlw91vf1srsh">
              <div className="max-w-xl" data-id="element-3914" data-mp="3dmo5kqbuqxih">
                <SectionLabel onDark data-id="element-3915" data-mp="3dpsfm4c00nh2">
                  {isEs ? '02 — Alcance' : '02 — Reach'}
                </SectionLabel>
                <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] text-white" data-id="element-3916" data-mp="33prwoji1k9de">
                  {isEs ? 'Explore la Red' : 'Explore the Network'}
                </h2>
              </div>
              <p className="font-nunito text-white/50 text-xs uppercase tracking-[0.16em] max-w-xs" data-id="element-3917" data-mp="11xwy95ksq1d4">
                {isEs ? 'Seleccione un punto para ver la organización y su trabajo en Honduras' : 'Select a point to see the organization and its work in Honduras'}
              </p>
            </div>

            <div className="rounded-xl overflow-hidden border border-white/12" style={{
            backgroundColor: NAVY_DEEP
          }} data-id="element-3918" data-mp="10foiys1twq1v">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10" data-id="element-3919" data-mp="rh36nemya79j">
                <span className="w-1.5 h-1.5 rounded-full" style={{
                backgroundColor: RED
              }} data-id="element-3920" data-mp="1o73t36zukbno" />
                <span className="font-nunito text-white/45 text-[10px] font-bold uppercase tracking-[0.22em]" data-id="element-3921" data-mp="1j897jxahw8zv">
                  {isEs ? 'Mapa de la Red' : 'Network Map'}
                </span>
              </div>
              <div className="h-[440px] sm:h-[560px] w-full" data-id="element-3922" data-mp="1adcrqlw9ry31">
                <Suspense fallback={<div className="w-full h-full flex items-center justify-center" style={{
                backgroundColor: NAVY_DEEP
              }} data-id="element-3924" data-mp="226lklu4j230q">
                      <p className="font-nunito text-white/40 text-xs uppercase tracking-[0.2em]" data-id="element-3925" data-mp="3ps6hbcirbuh3">
                        {isEs ? 'Cargando mapa…' : 'Loading map…'}
                      </p>
                    </div>} data-id="element-3923" data-mp="26xv6cjmygm7c">
                  <NetworkMap isEs={isEs} data-id="element-3926" data-mp="2o9utoadv7b8" />
                </Suspense>
              </div>
            </div>
          </div>

          {/* Member spotlights — expandable, logo-first */}
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 pb-20 lg:pb-24" data-id="element-3927" data-mp="ckmkmb2t510p">
            <div className="mb-8" data-id="element-3928" data-mp="hbuwddrm0yh1">
              <SectionLabel onDark data-id="element-3929" data-mp="hcwge7qlsvaw">
                {isEs ? 'Miembros de la Red' : 'Network Members'}
              </SectionLabel>
              <h3 className="font-bebas text-3xl sm:text-4xl tracking-[0.03em] leading-[0.98] text-white" data-id="element-3930" data-mp="lfssrgpjvv6e">
                {isEs ? 'Conozca a las Organizaciones' : 'Meet the Organizations'}
              </h3>
            </div>
            <NetworkOrganizations isEs={isEs} data-id="element-3931" data-mp="67pa5toq9lot" />
          </div>
        </section>

        {/* 3 — Year One participation */}
        <section style={{
        backgroundColor: PALE
      }} data-id="element-3932" data-mp="1c0211yn6wgw">
          <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-24" data-id="element-3933" data-mp="f9yd9yho2cs6">
            <div className="max-w-2xl mb-14" data-id="element-3934" data-mp="b7jypql15z6p">
              <SectionLabel data-id="element-3935" data-mp="b8v0jkux0wv6">
                {isEs ? '03 — Participación' : '03 — Participation'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95] mb-6" style={{
              color: NAVY
            }} data-id="element-3936" data-mp="3jlivgufttol9">
                {isEs ? 'Cómo Se Ve la Participación en el Primer Año' : 'What Participation Can Look Like in Year One'}
              </h2>
              <p className="font-nunito text-base leading-relaxed" style={{
              color: 'rgba(11,31,63,0.7)'
            }} data-id="element-3937" data-mp="2zyumjdv5df4b">
                {isEs ? 'Construyendo conexiones, oportunidades e impacto compartido. Las organizaciones participantes podrán involucrarse de maneras que se alineen con su misión, capacidad y prioridades.' : 'Building connections, opportunities, and shared impact together. Participating organizations will have opportunities to engage in ways that align with their mission, capacity, and priorities.'}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3938" data-mp="34ln4hgvq4poq">
              {PARTICIPATION.map((item, i) => {
              const Icon = item.icon;
              return <motion.div key={item.title} initial={{
                opacity: 0,
                y: 16
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true,
                margin: '-60px'
              }} transition={{
                duration: 0.45,
                delay: Math.min(i, 3) * 0.06,
                ease: EASE
              }} className="rounded-xl border bg-white p-7 h-full flex flex-col" style={{
                borderColor: 'rgba(11,31,63,0.12)'
              }} data-id="element-3939" data-mp="2um60r5018lve" data-mpi={i}>
                    <Icon className="w-5 h-5 mb-4" style={{
                  color: BLUE
                }} strokeWidth={1.7} data-id="element-3940" data-mp="f0fk032cqtni" />
                    <h3 className="font-bebas text-xl tracking-[0.06em] uppercase mb-3" style={{
                  color: NAVY
                }} data-id="element-3941" data-mp="5acre3z57hpp">
                      {isEs ? item.titleEs : item.title}
                    </h3>
                    <p className="font-nunito text-sm leading-relaxed" style={{
                  color: 'rgba(11,31,63,0.68)'
                }} data-id="element-3942" data-mp="cax0mw9nvyx">
                      {isEs ? item.textEs : item.text}
                    </p>
                  </motion.div>;
            })}
            </div>

            <p className="font-nunito text-sm sm:text-base leading-relaxed max-w-3xl mt-10" style={{
            color: 'rgba(11,31,63,0.68)'
          }} data-id="element-3943" data-mp="33wa2max2e5y7">
              {isEs ? 'La Red está diseñada para ser flexible. Las organizaciones pueden participar en las oportunidades más relevantes para su trabajo y capacidad. Nuestra meta es facilitar que las organizaciones se conecten, colaboren, compartan recursos y persigan oportunidades juntas.' : 'The Network is designed to be flexible. Organizations may participate in the opportunities most relevant to their work and capacity. Our goal is to make it easier for organizations to connect, collaborate, share resources, and pursue opportunities together.'}
            </p>
          </div>
        </section>

        {/* 5 — How do I join */}
        <section className="bg-white" data-id="element-3944" data-mp="9d3wb0jp5h50">
          <div className="max-w-4xl mx-auto px-5 sm:px-8 py-20 lg:py-24" data-id="element-3945" data-mp="5e4rdfxytkdk">
            <div className="text-center mb-14" data-id="element-3946" data-mp="cgt82sjp0u6m">
              <SectionLabel data-id="element-3947" data-mp="ci49wmtkvrv3">
                {isEs ? '04 — Membresía' : '04 — Membership'}
              </SectionLabel>
              <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[0.95]" style={{
              color: NAVY
            }} data-id="element-3948" data-mp="hg5u5b2qpx85">
                {isEs ? '¿Cómo Me Uno?' : 'How Do I Join?'}
              </h2>
            </div>

            <ol className="space-y-10" data-id="element-3949" data-mp="v82kpaorfz2f">
              {JOIN_STEPS.map((step, i) => <motion.li key={step.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-60px'
            }} transition={{
              duration: 0.45,
              delay: Math.min(i, 3) * 0.06,
              ease: EASE
            }} className="relative pl-16 sm:pl-20 pb-10 border-b last:border-b-0 last:pb-0" style={{
              borderColor: 'rgba(11,31,63,0.12)'
            }} data-id="element-3950" data-mp="105ui2k86cde0" data-mpi={i}>
                  <span className="absolute left-0 top-0 w-11 h-11 rounded-full flex items-center justify-center font-bebas text-lg tracking-wide text-white" style={{
                backgroundColor: i === 1 ? NAVY : BLUE
              }} aria-hidden="true" data-id="element-3951" data-mp="1oa3nv6pcw6oq">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <h3 className="font-bebas text-3xl tracking-[0.04em] mb-4" style={{
                color: BLUE
              }} data-id="element-3952" data-mp="2bxbrlk74z7on">
                    {isEs ? step.titleEs : step.title}
                  </h3>
                  <p className="font-nunito text-base leading-relaxed" style={{
                color: 'rgba(11,31,63,0.78)'
              }} data-id="element-3953" data-mp="1y0ohefq6al0s">
                    {isEs ? step.leadEs : step.lead}
                  </p>
                  {(isEs ? step.pointsEs : step.points).length > 0 ? <ul className="mt-5 space-y-3" data-id="element-3954" data-mp="2agmjozv4vgwp">
                      {(isEs ? step.pointsEs : step.points).map(point => <li key={point.slice(0, 40)} className="flex gap-3" data-id="element-3955" data-mp="2fa8zh0wlg0wq">
                          <CheckIcon className="w-4 h-4 mt-1 flex-shrink-0" style={{
                    color: RED
                  }} strokeWidth={2.5} data-id="element-3956" data-mp="21iaxda1av5a2" />
                          <span className="font-nunito text-sm leading-relaxed" style={{
                    color: 'rgba(11,31,63,0.68)'
                  }} data-id="element-3957" data-mp="2u3kkk9kf47lz">
                            {point}
                          </span>
                        </li>)}
                    </ul> : null}
                  {i === 0 ? <a href={joinHref} className="inline-flex items-center gap-2 mt-5 font-nunito text-[11px] font-bold uppercase tracking-[0.16em] transition-opacity hover:opacity-70" style={{
                color: BLUE
              }} data-id="element-3958" data-mp="1dr5iax36oh0t">
                      <MailIcon className="w-3.5 h-3.5" data-id="element-3959" data-mp="1f8c6xxsnix98" />
                      {isEs ? 'Solicitar información' : 'Request information'}
                    </a> : null}
                </motion.li>)}
            </ol>
          </div>
        </section>

        {/* 5 — CTA */}
        <section style={{
        backgroundColor: NAVY
      }} data-id="element-3960" data-mp="ii1xmqq0b1tm">
          <div className="max-w-3xl mx-auto px-5 sm:px-8 py-20 lg:py-24 text-center" data-id="element-3961" data-mp="ijmq8asrgaou">
            <h2 className="font-bebas text-4xl sm:text-5xl tracking-[0.03em] leading-[1.02] mb-7" data-id="element-3962" data-mp="wahzvpb32vth">
              <span className="block" style={{
              color: SKY
            }} data-id="element-3963" data-mp="rhovj2jj1phx">
                {isEs ? 'Organizaciones Más Fuertes' : 'Stronger Organizations'}
              </span>
              <span className="block text-white" data-id="element-3964" data-mp="3uzqadvnew0hl">
                {isEs ? 'Mayor Conexión' : 'Greater Connection'}
              </span>
              <span className="block" style={{
              color: RED
            }} data-id="element-3965" data-mp="1yvivki26tzwe">
                {isEs ? '¡Impacto Binacional!' : 'Bi-National Impact!'}
              </span>
            </h2>
            <p className="font-nunito text-white/70 text-base sm:text-lg leading-relaxed mb-9" data-id="element-3966" data-mp="28llneexkp7t5">
              {isEs ? 'Queremos construir esto con ustedes, no simplemente para ustedes. Si su organización sirve a las comunidades hondureñas o hondureño-americanas, nos encantaría conocerla.' : 'We want to build this with you, not simply for you. If your organization serves Honduran or Honduran-American communities, we would be honored to know you.'}
            </p>
            <a href={joinHref} className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-white font-bold text-xs uppercase tracking-[0.16em] transition-colors" style={{
            color: NAVY
          }} onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = RED;
            e.currentTarget.style.color = '#ffffff';
          }} onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = '#ffffff';
            e.currentTarget.style.color = NAVY;
          }} data-id="element-3967" data-mp="1txqt4u3x2vdy">
              <MailIcon className="w-4 h-4" data-id="element-3968" data-mp="fy5trn2qsl4e" />
              {CONTACT_EMAIL}
            </a>
          </div>
        </section>
      </main>

      <footer className="bg-white py-9 border-t" style={{
      borderColor: 'rgba(11,31,63,0.12)'
    }} data-id="element-3969" data-mp="azcebp3wak1l">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 lg:px-12 flex flex-wrap items-center justify-between gap-5" data-id="element-3970" data-mp="5yorzgqm2rdp">
          <img src={NETWORK_LOGO} alt="Honduran American Association National Network" className="h-12 w-auto object-contain" data-id="element-3971" data-mp="1y4rq4aqr7nc" />
          <p className="font-nunito text-[10px] uppercase tracking-[0.22em]" style={{
          color: 'rgba(11,31,63,0.5)'
        }} data-id="element-3972" data-mp="1wauk2hlgo8sm">
            {isEs ? 'Una iniciativa de la Asociación Hondureño-Americana' : 'An initiative of the Honduran-American Association'}
          </p>
        </div>
      </footer>
    </div>;
}